import React from 'react';
import { Close } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { GridProps, IconButton, Popper, PopperProps } from '@mui/material';
import SparkTabs, { TabItem } from 'sharedComponents/UI/SparkTabs';
import SparkStepAccordion from './SparkStepAccordion';
import InputReferenceAccordion from './InputReferenceAccordion';
import FieldSkeleton from 'sharedComponents/Skeleton/FieldSkeleton';
import {
  useSparkOutputReferences,
  useSparkStepReferences,
} from 'hooks/SparkBuilder/sparkStepReferences';
import type { SparkReferenceValue } from 'types/SparkStepReference';
import { useSparkBuilderStore } from 'stores';
import DownloadIcon from 'components/SVG/DownloadIcon';
import ListIcon from 'components/SVG/ListIcon';

type StepReferencePopupProps = {
  onClose: () => void;
  sx?: GridProps['sx'];
  handleTagClick: (value: SparkReferenceValue) => void;
  anchorEl?: HTMLElement | null;
  placement?: PopperProps['placement'];
};

function StepReferencePopup({
  onClose,
  sx,
  handleTagClick,
  anchorEl,
  placement = 'bottom',
}: StepReferencePopupProps) {
  const { id: paramSparkID = '' } = useParams();

  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);

  const storeSparkID = useSparkBuilderStore((store) => store.sparkID);

  const sparkID = storeSparkID || paramSparkID;

  const selectedStep = useSparkBuilderStore(
    (store) => store.builderSideBar.selectedStep
  );
  const sparkStep = useSparkBuilderStore((store) =>
    store.sparkStepsActions.getSparkStep(selectedStep?.id || '')
  );

  const currentStepType = selectedStep?.type;

  const {
    sparkSteps: selectedStepReferences,
    isLoading: isSelectedStepReferencesLoading,
    sparkInputParameters,
  } = useSparkStepReferences(selectedStep?.id);

  const {
    sparkSteps: allStepReferences,
    sparkInputParameters: allsparkInputParameters,
    isLoading: isReferencesLoading,
  } = useSparkOutputReferences(sparkID, { enabled: !selectedStep?.id });

  const stepReferences = React.useMemo(() => {
    if (currentStepType !== 'sparkStep') {
      return allStepReferences;
    }
    if (!sparkStep) {
      return;
    }
    return selectedStepReferences;
  }, [allStepReferences, currentStepType, selectedStepReferences, sparkStep]);

  const inputReference = React.useMemo(() => {
    if (currentStepType !== 'sparkStep') {
      return allsparkInputParameters;
    }
    if (!sparkStep) {
      return;
    }
    return sparkInputParameters;
  }, [
    allsparkInputParameters,
    currentStepType,
    sparkInputParameters,
    sparkStep,
  ]);

  const tabItems: TabItem[] = React.useMemo(() => {
    const items = [
      {
        label: '',
        icon: (
          <DownloadIcon
            width='24px'
            height='24px'
            {...(selectedTabIndex !== 0 ? { color: 'white' } : {})}
          />
        ),
        content:
          isReferencesLoading || isSelectedStepReferencesLoading ? (
            <FieldSkeleton count={3} />
          ) : (
            <InputReferenceAccordion
              inputReference={inputReference}
              onTagClick={handleTagClick}
            />
          ),
      },
    ];
    if (sparkStep?.attributes.stepOrder !== 1) {
      items.push({
        icon: (
          <ListIcon {...(selectedTabIndex !== 1 ? { color: 'white' } : {})} />
        ),
        label: '',
        content:
          isReferencesLoading || isSelectedStepReferencesLoading ? (
            <FieldSkeleton count={3} />
          ) : (
            <SparkStepAccordion
              sparkSteps={stepReferences}
              onTagClick={handleTagClick}
            />
          ),
      });
    }
    return items;
  }, [
    handleTagClick,
    inputReference,
    isReferencesLoading,
    isSelectedStepReferencesLoading,
    selectedTabIndex,
    sparkStep?.attributes.stepOrder,
    stepReferences,
  ]);

  return (
    <Popper
      open={true}
      anchorEl={anchorEl}
      placement={placement}
      sx={{
        zIndex: 500000,
        // minWidth: '400px',
        // maxWidth: '600px',
        width: '500px',
        borderRadius: '10px',
        overflow: 'hidden',
        marginRight: '5px !important',
        userSelect: 'none',
      }}
    >
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          onClose();
        }}
        sx={{ position: 'absolute', top: '12px', right: '6px', zIndex: 1 }}
      >
        <Close
          sx={{
            width: '24px',
            height: '24px',
            color: 'white',
          }}
        />
      </IconButton>
      <SparkTabs
        onChange={setSelectedTabIndex}
        tabItems={tabItems}
        tabStyles={{
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
          fontSize: '16px',
          color: '#000000',
          textTransform: 'capitalize',
          gap: 2,
          minWidth: 50,
          '&.Mui-selected': {
            backgroundColor: 'white',
            color: '#000000',
          },
        }}
        tabPanelStyles={{
          width: '100%',
          backgroundColor: 'white',
          padding: '24px 7px',
          border: '1px solid #e0e0e0',
          borderTop: 'none',
          borderRadius: '0 0 10px 10px',
        }}
        tabHeaderStyles={{
          bgcolor: (theme) => theme.palette.primary.main,
          padding: 2,
          paddingBottom: 0,
        }}
        tabNavigationContainerStyles={{
          borderBottom: 'none',
        }}
      />
    </Popper>
  );
}

export default React.memo(StepReferencePopup);
